import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Alert } from "react-bootstrap";
import API from "../../services/api";
import { t } from "@lingui/macro";
import { getLocalizedField } from "../../i18n/backendTranslations";

const TicketFormOffline = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotionInfo, setPromotionInfo] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [ticketIds, setTicketIds] = useState([]);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await API.get("/api/tickets/public/prices");
        if (Array.isArray(response.data)) {
          setPrices(response.data);
          setPromotionInfo(
            response.data.map((price) => {
              if (price.freeItemPromoActive && price.priceCategory !== "Free") {
                return {
                  priceId: price.id,
                  promoMessage: t`Buy ${price.quantityForFreeItem} tickets, get 1 free!`,
                };
              }
              return { priceId: price.id, promoMessage: "" };
            })
          );
        } else {
          setPrices([]);
        }
      } catch (error) {
        setPrices([]);
        console.error(t`Error fetching prices:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, []);

  const initialValues = {
    email: "office-sold-tickets@elarenal.com",
    isGroup: false,
    paymentMethod: "",
    tickets: prices.reduce((acc, price) => {
      acc[price.id] = {
        priceId: price.id,
        priceCategory: price.priceCategory,
        quantity: 0,
      };
      return acc;
    }, {}),
    
  };

  const validationSchema = Yup.object().shape({
    paymentMethod: Yup.string().required(t`Please select a payment method`),
    tickets: Yup.object()
      .shape(
        Object.keys(initialValues.tickets).reduce((acc, key) => {
          acc[key] = Yup.object().shape({
            quantity: Yup.number()
              .integer()
              .min(0, t`Minimum quantity is 0`)
              .required(t`Quantity is required`),
          });
          return acc;
        }, {})
      )
      .test(
        "at-least-one-ticket",
        t`At least one ticket must be selected`,
        (tickets) => Object.values(tickets).some((ticket) => ticket.quantity > 0)
      )
      .required(),
  });

  const calculateFreeTickets = (tickets) => {
    Object.values(tickets).forEach((ticket) => {
      const price = prices.find((p) => p.id === ticket.priceId);
      if (price && price.freeItemPromoActive && ticket.quantity >= price.quantityForFreeItem) {
        const freeTickets = Math.floor(ticket.quantity / price.quantityForFreeItem);
        ticket.quantity += freeTickets;
      }
    });
  };

  const calculateTotal = (tickets) => {
    let totalAmount = 0;
    Object.values(tickets).forEach((ticket) => {
      const price = prices.find((p) => p.id === ticket.priceId);
      if (price) {
        totalAmount += ticket.quantity * price.finalPrice;
      }
    });
    return Number(totalAmount.toFixed(2));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("🧾 Offline form submitted:", values);

    calculateFreeTickets(values.tickets);
    const totalAmount = calculateTotal(values.tickets);

    const ticketOrderRequest = {
      email: values.email,
      isGroup: values.isGroup,
      paymentMethod: values.paymentMethod, // cash | card
      total: totalAmount,
      tickets: Object.values(values.tickets)
        .filter((ticket) => ticket.quantity > 0)
        .map((ticket) => ({
          priceId: ticket.priceId,
          priceCategory: ticket.priceCategory,
          quantity: ticket.quantity,
        }))
     
    };

    console.log("📦 Ticket order payload:", ticketOrderRequest);

    try {
      const response = await API.post("/api/tickets/sell-tickets", ticketOrderRequest);
      if (response.data && response.data.length > 0) {
        setTicketIds(response.data);
        alert(t`Tickets generated successfully!`);
      } else {
        setErrorMessage(t`No tickets were generated.`);
      }
    } catch (error) {
      console.error(t`Error during ticket sale:`, error);
      setErrorMessage(t`An error occurred while processing the ticket sale.`);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTicketPdf = async (ticketId) => {
    try {
      const response = await API.get(`/api/users/tickets/${ticketId}/download`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ticket-${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error(t`Error downloading ticket:`, error);
      setErrorMessage(t`An error occurred while downloading the ticket.`);
    }
  };

  return (
    <div className="form-container">
      {loading ? (
        <div>{t`Loading...`}</div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ setFieldValue, values, isSubmitting }) => {
            const total = calculateTotal(values.tickets);

            return (
              <Form>
                <h2>{t`Offline Ticket Sale`}</h2>

                <div className="group-ticket-container">
                  <label className="form-label">
                    {t`Group ticket`}
                  </label>
                  <Field type="checkbox" name="isGroup" />
                  <span className="group-ticket-info">
                    {t`Places all guests on one ticket`}
                  </span>
                </div>

                {Object.entries(prices)
                  .filter(([_, price]) => price.id !== 14)
                  .map(([_, price]) => {
                    const promoMessage =
                      promotionInfo.find((promo) => promo.priceId === price.id)?.promoMessage || "";

                    return (
                      <div key={price.id} className="ticket-row">
                        <div className="ticket-header">
                          <h3 className="ticket-category">
                            {getLocalizedField(price, "priceCategory")}
                          </h3>
                          <span className="ticket-price">
                            {price.hasPromotion ? (
                              <>
                                <span style={{ textDecoration: "line-through", marginRight: "10px" }}>
                                  {price.originalPrice} {price.currency}
                                </span>
                                <span style={{ color: "red" }}>
                                  {price.finalPrice} {price.currency}
                                </span>
                                {promoMessage && (
                                  <div className="promotion-info">
                                    <strong>{promoMessage}</strong>
                                  </div>
                                )}
                              </>
                            ) : (
                              <span>
                                {price.originalPrice} {price.currency}
                              </span>
                            )}
                          </span>
                        </div>

                        <div className="quantity-container">
                          <Field
                            type="number"
                            name={`tickets.${price.id}.quantity`}
                            className="form-control small-field"
                            placeholder="0"
                            min="0"
                            onChange={(e) =>
                              setFieldValue(
                                `tickets.${price.id}.quantity`,
                                parseInt(e.target.value, 10) || 0
                              )
                            }
                          />
                          <ErrorMessage
                            name={`tickets.${price.id}.quantity`}
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>
                    );
                  })}

                <div className="payment-method-container">
                  <label className="form-label">{t`Payment Method`}</label>
                  <div>
                    <label>
                      <Field type="radio" name="paymentMethod" value="cash" /> {t`Cash`}
                    </label>
                    <label>
                      <Field type="radio" name="paymentMethod" value="card" /> {t`Card`}
                    </label>
                  </div>
                  <ErrorMessage name="paymentMethod" component="div" className="error-message" />
                </div>

                <div className="total-price">
                  <p>{t`Total:`} {total} MXN</p>
                </div>

                <div className="form-actions">
                  <button type="submit" className="add-new-btn" disabled={isSubmitting}>
                    {t`Confirm Sale`}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}

      {ticketIds.length > 0 && (
        <div className="ticket-ids">
          <h3>{t`Your Generated Tickets`}</h3>
          <ul>
            {ticketIds.map((ticketId) => (
              <li key={ticketId}>
                <button onClick={() => downloadTicketPdf(ticketId)}>
                  {t`Download Ticket`} {ticketId}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
    </div>
  );
};

export default TicketFormOffline;
